/* ============================================================================
   Importación de aeronaves desde planillas del operador
   ----------------------------------------------------------------------------
   Convierte lo que el operador ya tiene —status report, tabla de peso máximo,
   tabla de consumo, envolvente— en las estructuras que la aplicación usa:
   un PERFIL de tipo y una entrada de FLOTA.

   REGLAS QUE ESTE MÓDULO NO NEGOCIA
   ---------------------------------
   1. No se inventa ningún dato. Lo que no viene queda en `null`, y la
      aplicación muestra el hueco. Ver docs/decisiones.md — AD-004.

   2. Una celda de performance que viola la física —el peso máximo no puede
      subir con la temperatura ni con la altitud— se ANULA y se reporta. Es
      exactamente lo que ya se hizo a mano con las cinco celdas del AS350 B3 en
      cartas/as350b3.js: un hueco manda al manual, un número alto de más
      autoriza carga que la aeronave no sostiene.

   3. Toda importación entra marcada como NO VERIFICADA. Ningún test detecta
      una tabla mal transcrita; solo el cotejo humano contra el RFM. Ver AD-005.

   4. Una fila ilegible no puede tumbar la importación completa: va a
      cuarentena, se reporta, y el resto se carga.

   Estas funciones son puras: reciben matrices (arreglo de filas) y devuelven
   objetos. La lectura de archivos vive en features/importador/.
   ========================================================================== */

/* -------------------------------------------------------------- conversores */

/**
 * Número tolerante con lo que traen las planillas reales.
 *
 * Acepta "1.234,5" (decimal español), "1,234.5" (inglés), "1200 kg", " 12 ",
 * y devuelve null —no 0— para "N/A", "—", vacío o texto. La diferencia
 * importa: 0 es un peso, null es "no lo tengo".
 *
 * REGLA DEL PUNTO SOLO — decidida, no accidental
 * ---------------------------------------------
 * "1.234" es ambiguo: 1234 a la española, 1,234 a la inglesa. Acá el punto
 * SIEMPRE es decimal. El motivo es concreto: los brazos y los límites de CG de
 * esta aplicación tienen esa forma exacta —3.425 m, 3.17 m— y leerlos como
 * miles convertiría un centro de gravedad de 3,425 m en 3425 m. Un peso mal
 * leído por mil se nota; un CG mal leído por mil también, pero la probabilidad
 * de encontrarse "3.425" como brazo es mucho mayor que la de encontrarse
 * "1.234" como peso escrito a la española en una celda de texto.
 *
 * La coma sí se interpreta según el contexto, porque ahí no hay ambigüedad
 * destructiva. Y `validarImportacion` avisa cuando un peso o un brazo salen
 * fuera de rango creíble, que es donde se caza el error si igual ocurre.
 */
export function aNumero(v, { permitirNegativo = false } = {}) {
  try {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    let s = String(v).trim();
    if (!s) return null;
    if (/^(n\/?a|na|nd|s\/?d|-{1,3}|—|–|\?|sin dato|pendiente)$/i.test(s)) return null;
    s = s.replace(/[^0-9,.\-+]/g, "");
    if (!s || s === "-" || s === "+") return null;
    const comas = (s.match(/,/g) || []).length;
    const puntos = (s.match(/\./g) || []).length;
    if (comas && puntos) {
      if (s.lastIndexOf(",") > s.lastIndexOf(".")) s = s.replace(/\./g, "").replace(",", ".");
      else s = s.replace(/,/g, "");
    } else if (comas === 1 && /,\d{1,3}$/.test(s)) {
      s = s.replace(",", ".");
    } else if (comas >= 1) {
      s = s.replace(/,/g, "");
    }
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return null;
    if (!permitirNegativo && n < 0) return null;
    return n;
  } catch {
    return null;
  }
}

export function aTexto(v) {
  try {
    if (v === null || v === undefined) return "";
    return String(v).trim().replace(/\s+/g, " ");
  } catch {
    return "";
  }
}

export function normalizar(s) {
  return aTexto(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Fecha en ISO (YYYY-MM-DD) o null.
 *
 * Acepta el serial de Excel, ISO, y dd/mm/yyyy. Ante ambigüedad real
 * (03/04/2026) asume dd/mm, que es lo que usan las planillas de la región.
 */
export function aFecha(v) {
  try {
    if (v === null || v === undefined || v === "") return null;
    if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
    if (typeof v === "number" && v > 20000 && v < 80000) {
      const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    const s = aTexto(v);
    if (!s) return null;
    let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (m) {
      const mes = Number(m[2]), dia = Number(m[3]);
      if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
      return `${m[1]}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
    }
    m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
    if (m) {
      let [, a, b, y] = m;
      if (y.length === 2) y = (Number(y) > 70 ? "19" : "20") + y;
      let dia = Number(a), mes = Number(b);
      if (mes > 12 && dia <= 12) { const t = dia; dia = mes; mes = t; }
      if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
      return `${y}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
    }
    return null;
  } catch {
    return null;
  }
}

const LB_A_KG = 0.45359237;
const IN_A_M = 0.0254;

/** Factor para llevar a kilos según lo que diga el encabezado. */
export function factorPeso(encabezado) {
  return /\b(lb|lbs|libra|libras|pound|pounds)\b/.test(normalizar(encabezado)) ? LB_A_KG : 1;
}

/** Factor para llevar a metros según lo que diga el encabezado. */
export function factorLongitud(encabezado) {
  const h = normalizar(encabezado);
  if (/\b(in|inch|inches|pulg|pulgada|pulgadas)\b/.test(h)) return IN_A_M;
  if (/\bmm\b/.test(h)) return 0.001;
  if (/\bcm\b/.test(h)) return 0.01;
  return 1;
}

/* -------------------------------------------------- clasificación de hojas */

export const TIPOS_HOJA = {
  identificacion: "Identificación y pesaje",
  estaciones: "Brazos y compartimentos",
  envolvente: "Envolvente de CG",
  oge: "Peso máximo OGE",
  ige: "Peso máximo IGE",
  combustible: "Consumo de combustible",
  status: "Status report / mantenimiento",
};

const CLAVES = {
  identificacion: ["matricula", "registration", "certificaciones", "peso basico", "peso vacio", "empty weight", "pesaje", "fecha de pesaje", "serie", "msn", "mtow", "peso maximo despegue"],
  estaciones: ["brazo", "arm", "estacion", "station", "compartimento", "compartment", "equipaje", "baggage", "asiento", "seat", "piloto", "pilot", "datum"],
  envolvente: ["envolvente", "envelope", "cg", "centro de gravedad", "limite delantero", "limite trasero", "forward", "rearward", "fwd", "aft"],
  oge: ["oge", "hoge", "out of ground", "fuera de efecto suelo", "sin efecto suelo"],
  ige: ["ige", "hige", "in ground", "en efecto suelo", "con efecto suelo"],
  combustible: ["consumo", "fuel flow", "fuel consumption", "kg h", "lb h", "combustible"],
  status: ["status", "tsn", "tso", "csn", "tbo", "componente", "component", "part number", "vencimiento", "inspeccion", "ciclos", "cycles", "ng", "nf"],
};

/**
 * Clasifica una hoja por sus encabezados y su nombre.
 * Devuelve { tipo, confianza } con tipo=null si no alcanza para decidir.
 */
export function detectarTipoHoja(encabezados = [], nombreHoja = "") {
  const texto = normalizar([...encabezados, nombreHoja, nombreHoja].join(" "));
  const puntajes = {};
  for (const [tipo, claves] of Object.entries(CLAVES)) {
    let p = 0;
    for (const c of claves) if (texto.includes(c)) p += c.length > 4 ? 2 : 1;
    puntajes[tipo] = p;
  }
  const orden = Object.entries(puntajes).sort((a, b) => b[1] - a[1]);
  const [mejor, puntaje] = orden[0];
  const segundo = orden[1] ? orden[1][1] : 0;
  if (puntaje < 2) return { tipo: null, confianza: 0, puntajes };
  return { tipo: mejor, confianza: Math.min(1, (puntaje - segundo * 0.5) / 5), puntajes };
}

/** Índice de la fila que parece el encabezado de la tabla (título y logo arriba). */
export function ubicarEncabezado(matriz = []) {
  let mejorIdx = 0, mejorPuntaje = -1;
  for (let i = 0; i < Math.min(matriz.length, 25); i++) {
    const fila = matriz[i] || [];
    const llenas = fila.filter((c) => aTexto(c) !== "").length;
    const textos = fila.filter((c) => aTexto(c) !== "" && aNumero(c) === null).length;
    const puntaje = llenas + textos * 2;
    if (llenas >= 2 && puntaje > mejorPuntaje) { mejorPuntaje = puntaje; mejorIdx = i; }
  }
  return mejorIdx;
}

function buscarColumna(encabezados, patrones) {
  for (const p of patrones) {
    const i = encabezados.findIndex((h) => new RegExp(p).test(normalizar(h)));
    if (i >= 0) return i;
  }
  return -1;
}

const vacia = (fila) => !fila || fila.every((c) => aTexto(c) === "");

/* ------------------------------------------------------------ rejilla alt×temp */

/**
 * Lee una tabla de doble entrada: altitudes en la primera columna,
 * temperaturas en la fila de encabezado. Es la forma de ogeTable, igeTable y
 * fuelTable.
 *
 * Devuelve { alts, temps, rows } o null si no hay una rejilla reconocible.
 */
export function leerRejilla(matriz = [], cuarentena = [], origen = "") {
  try {
    let filaEnc = -1;
    let temps = null;
    for (let i = 0; i < Math.min(matriz.length, 25); i++) {
      const fila = matriz[i] || [];
      const candidatas = [];
      for (let c = 1; c < fila.length; c++) {
        const n = aNumero(fila[c], { permitirNegativo: true });
        if (n !== null && n >= -60 && n <= 60) candidatas.push({ c, n });
        else if (aTexto(fila[c]) !== "") candidatas.length = 0;
      }
      if (candidatas.length >= 3) {
        const crecientes = candidatas.every((x, k) => k === 0 || x.n > candidatas[k - 1].n);
        if (crecientes) { filaEnc = i; temps = candidatas; break; }
      }
    }
    if (filaEnc < 0 || !temps) return null;

    const alts = [];
    const rows = [];
    for (let i = filaEnc + 1; i < matriz.length; i++) {
      const fila = matriz[i] || [];
      if (vacia(fila)) continue;
      const alt = aNumero(fila[0]);
      if (alt === null) {
        if (aTexto(fila[0]) !== "") {
          cuarentena.push({ origen, fila: i + 1, motivo: "Altitud no legible en la primera columna", datos: aTexto(fila[0]) });
        }
        continue;
      }
      const valores = temps.map(({ c }) => {
        const n = aNumero(fila[c]);
        return n === null ? null : n;
      });
      if (valores.every((v) => v === null)) continue;
      alts.push(alt);
      rows.push(valores);
    }
    if (alts.length < 2) return null;
    return { alts, temps: temps.map((t) => t.n), rows };
  } catch (e) {
    cuarentena.push({ origen, fila: "-", motivo: "La rejilla falló completa: " + (e && e.message), datos: "" });
    return null;
  }
}

/**
 * Anula las celdas que violan la física de una carta de peso máximo:
 * el peso admisible NO puede subir cuando sube la temperatura ni cuando sube
 * la altitud. Las celdas que lo hacen quedan en null y se reportan.
 *
 * Solo aplica a cartas de PESO (OGE/IGE). El consumo se comporta al revés y
 * no se toca.
 */
export function anularCeldasImposibles(rejilla, nombre = "carta") {
  if (!rejilla) return { rejilla, anuladas: [] };
  const { alts, temps, rows } = rejilla;
  const copia = rows.map((f) => f.slice());
  const anuladas = [];

  // Sube con la temperatura (dentro de la misma altitud)
  for (let i = 0; i < copia.length; i++) {
    for (let j = 1; j < copia[i].length; j++) {
      const previo = copia[i][j - 1], actual = copia[i][j];
      if (previo !== null && actual !== null && actual > previo) {
        anuladas.push({ carta: nombre, alt: alts[i], temp: temps[j], valor: actual, motivo: `sube ${actual - previo} kg respecto a ${temps[j - 1]} °C` });
        copia[i][j] = null;
      }
    }
  }
  // Sube con la altitud (dentro de la misma temperatura)
  for (let j = 0; j < temps.length; j++) {
    for (let i = 1; i < copia.length; i++) {
      const previo = copia[i - 1][j], actual = copia[i][j];
      if (previo !== null && actual !== null && actual > previo) {
        anuladas.push({ carta: nombre, alt: alts[i], temp: temps[j], valor: actual, motivo: `sube ${actual - previo} kg respecto a ${alts[i - 1]} ft` });
        copia[i][j] = null;
      }
    }
  }
  return { rejilla: { alts, temps, rows: copia }, anuladas };
}

/* ------------------------------------------------------------ identificación */

const CAMPOS_ID = {
  matricula: ["matricula", "registration", "^reg$"],
  tipo: ["tipo de aeronave", "modelo", "^model$", "aircraft type", "^tipo$"],
  serie: ["numero de serie", "^serie$", "^serial", "msn"],
  motorTipo: ["tipo de motor", "^motor$", "^engine$", "powerplant"],
  motorSerie: ["serie motor", "serie del motor", "engine serial"],
  pesoBasicoVacioKg: ["peso basico vacio", "peso vacio", "basic empty", "empty weight", "peso basico"],
  brazoVacioM: ["brazo vacio", "brazo basico", "empty arm", "cg vacio"],
  pesoMaxDespegueKg: ["peso maximo de despegue", "peso max despegue", "mtow", "max gross", "peso maximo"],
  pesoCeroCombustibleKg: ["peso cero combustible", "zero fuel"],
  fechaPesaje: ["fecha de pesaje", "fecha pesaje", "ultimo pesaje", "weighing date"],
  tsn: ["^tsn$", "total time", "tiempo total", "horas totales"],
  csn: ["^csn$", "ciclos totales", "total cycles"],
  fuelCapacityL: ["capacidad de combustible", "combustible maximo", "fuel capacity"],
};

const NUMERICOS = new Set(["pesoBasicoVacioKg", "brazoVacioM", "pesoMaxDespegueKg", "pesoCeroCombustibleKg", "tsn", "csn", "fuelCapacityL"]);

/**
 * Lee pares etiqueta/valor de cualquier hoja. Las fichas de aeronave rara vez
 * son tablas: son "Matrícula | HC-XXX" en dos columnas.
 */
export function leerIdentificacion(matriz = []) {
  const out = {};
  try {
    for (const fila of matriz) {
      if (!Array.isArray(fila)) continue;
      for (let c = 0; c < fila.length - 1; c++) {
        const etiqueta = normalizar(fila[c]);
        if (!etiqueta) continue;
        for (const [campo, patrones] of Object.entries(CAMPOS_ID)) {
          if (out[campo] !== undefined && out[campo] !== null) continue;
          if (!patrones.some((p) => new RegExp(p).test(etiqueta))) continue;
          let bruto = null;
          for (let d = c + 1; d < Math.min(fila.length, c + 4); d++) {
            if (aTexto(fila[d]) !== "") { bruto = fila[d]; break; }
          }
          if (bruto === null) continue;
          if (campo === "fechaPesaje") out[campo] = aFecha(bruto);
          else if (NUMERICOS.has(campo)) {
            const unidad = aTexto(fila[c]);
            const n = aNumero(bruto, { permitirNegativo: campo === "brazoVacioM" });
            if (n === null) continue;
            out[campo] = campo === "brazoVacioM" ? +(n * factorLongitud(unidad)).toFixed(4)
              : /Kg$/.test(campo) ? +(n * factorPeso(unidad)).toFixed(2)
              : n;
          } else out[campo] = aTexto(bruto);
        }
      }
    }
  } catch { /* identificación parcial es aceptable; nunca tumba la importación */ }
  return Object.keys(out).length ? out : null;
}

/* --------------------------------------------------------------- estaciones */

const MAPA_BRAZOS = [
  { clave: "pilot", patrones: ["piloto", "pilot"] },
  { clave: "paxFront", patrones: ["delantero", "front", "copiloto"] },
  { clave: "paxRear", patrones: ["trasero.*asiento", "asiento.*trasero", "rear seat", "pax rear", "banqueta"] },
  { clave: "bagLat", patrones: ["lateral", "derecha", "izquierda", "lat$", "side"] },
  { clave: "bagRear", patrones: ["equipaje trasero", "bag rear", "maletero", "compartimento trasero"] },
  { clave: "external", patrones: ["externa", "gancho", "hook", "sling", "eslinga"] },
  { clave: "fuel", patrones: ["combustible", "fuel", "tanque"] },
  { clave: "canasta", patrones: ["canasta", "basket"] },
];

/**
 * Lee brazos y compartimentos. Devuelve { arms, cargoCompartments, sinMapear }.
 * `sinMapear` son estaciones que el operador tiene y la app todavía no modela:
 * se listan en pantalla en vez de descartarlas en silencio.
 */
export function leerEstaciones(encabezados = [], filas = [], cuarentena = [], origen = "") {
  const cNom = buscarColumna(encabezados, ["estacion", "descripcion", "^item$", "nombre", "asiento", "compartimento", "station"]);
  const cBrazo = buscarColumna(encabezados, ["brazo", "arm", "^sta$"]);
  const cMax = buscarColumna(encabezados, ["peso max", "^max", "capacidad", "limite"]);
  if (cNom < 0 || cBrazo < 0) return null;

  const fl = factorLongitud(encabezados[cBrazo]);
  const fp = cMax >= 0 ? factorPeso(encabezados[cMax]) : 1;
  const arms = {};
  const cargoCompartments = [];
  const sinMapear = [];

  filas.forEach((fila, i) => {
    try {
      if (vacia(fila)) return;
      const nombre = aTexto(fila[cNom]);
      const brazo = aNumero(fila[cBrazo], { permitirNegativo: true });
      if (!nombre || brazo === null) {
        cuarentena.push({ origen, fila: i + 2, motivo: "Estación sin nombre o sin brazo legible", datos: (fila || []).slice(0, 4).map(aTexto).join(" | ") });
        return;
      }
      const brazoM = +(brazo * fl).toFixed(4);
      const maxKg = cMax >= 0 && aNumero(fila[cMax]) !== null ? +(aNumero(fila[cMax]) * fp).toFixed(2) : null;
      const n = normalizar(nombre);
      const encontrado = MAPA_BRAZOS.find((m) => m.patrones.some((p) => new RegExp(p).test(n)));
      if (encontrado) {
        if (arms[encontrado.clave] === undefined) arms[encontrado.clave] = brazoM;
      } else {
        sinMapear.push({ nombre, brazoM, maxKg });
      }
      if (/equipaje|compartimento|bagg?age|carga interna|maletero|canasta/.test(n) && maxKg !== null) {
        const esTrasero = /trasero|rear/.test(n);
        cargoCompartments.push({
          key: n.replace(/[^a-z0-9]/g, "_").slice(0, 24) || `comp_${cargoCompartments.length + 1}`,
          label: nombre,
          maxKg,
          armKey: esTrasero ? "bagRear" : "bagLat",
        });
      }
    } catch (e) {
      cuarentena.push({ origen, fila: i + 2, motivo: "Error al leer la estación: " + (e && e.message), datos: "" });
    }
  });

  if (!Object.keys(arms).length && !cargoCompartments.length && !sinMapear.length) return null;
  return { arms, cargoCompartments, sinMapear };
}

/* --------------------------------------------------------------- envolvente */

/**
 * Envolvente de CG. Acepta dos formas:
 *   a) peso | límite delantero | límite trasero            (dos curvas)
 *   b) peso | delantero | trasero interno | trasero externo (tres curvas)
 *
 * Devuelve { forward, rearwardInternal, rearwardExternal } con pares
 * [peso, cg], que es lo que consume limitAtWeight().
 */
export function leerEnvolvente(encabezados = [], filas = [], cuarentena = [], origen = "") {
  const cPeso = buscarColumna(encabezados, ["peso", "weight"]);
  const cFwd = buscarColumna(encabezados, ["delantero", "forward", "fwd", "minimo", "^min"]);
  const cInt = buscarColumna(encabezados, ["trasero interno", "interna", "internal", "rearward internal"]);
  const cExt = buscarColumna(encabezados, ["trasero externo", "externa", "external", "rearward external", "carga externa"]);
  const cAft = buscarColumna(encabezados, ["trasero", "rearward", "aft", "maximo", "^max"]);
  if (cPeso < 0 || cFwd < 0) return null;

  const fp = factorPeso(encabezados[cPeso]);
  const fl = factorLongitud(encabezados[cFwd]);
  const forward = [], rearwardInternal = [], rearwardExternal = [];

  filas.forEach((fila, i) => {
    try {
      if (vacia(fila)) return;
      const peso = aNumero(fila[cPeso]);
      const fwd = aNumero(fila[cFwd], { permitirNegativo: true });
      if (peso === null || fwd === null) {
        cuarentena.push({ origen, fila: i + 2, motivo: "Punto de envolvente incompleto", datos: (fila || []).slice(0, 4).map(aTexto).join(" | ") });
        return;
      }
      const p = +(peso * fp).toFixed(2);
      forward.push([p, +(fwd * fl).toFixed(4)]);
      const interno = cInt >= 0 ? aNumero(fila[cInt], { permitirNegativo: true })
        : cAft >= 0 ? aNumero(fila[cAft], { permitirNegativo: true }) : null;
      if (interno !== null) rearwardInternal.push([p, +(interno * fl).toFixed(4)]);
      const externo = cExt >= 0 ? aNumero(fila[cExt], { permitirNegativo: true }) : null;
      if (externo !== null) rearwardExternal.push([p, +(externo * fl).toFixed(4)]);
    } catch {
      cuarentena.push({ origen, fila: i + 2, motivo: "Error al leer el punto de envolvente", datos: "" });
    }
  });

  if (forward.length < 2) return null;
  const ordenar = (a) => a.sort((x, y) => x[0] - y[0]);
  return {
    forward: ordenar(forward),
    rearwardInternal: ordenar(rearwardInternal),
    // Sin columna de carga externa no se inventa una curva: se deja la interna,
    // que es la más restrictiva. Nunca al revés.
    rearwardExternal: rearwardExternal.length >= 2 ? ordenar(rearwardExternal) : ordenar(rearwardInternal.slice()),
  };
}

/* ------------------------------------------------------------------- status */

/**
 * Status report: componentes con horas/ciclos y sus límites.
 * Devuelve { items, totales } — `totales` alimenta TSN/CSN de la flota.
 */
export function leerStatus(encabezados = [], filas = [], cuarentena = [], origen = "") {
  const cComp = buscarColumna(encabezados, ["componente", "component", "descripcion", "description", "^item$", "parte", "tarea", "inspeccion"]);
  if (cComp < 0) return null;
  const cPn = buscarColumna(encabezados, ["part number", "^pn$", "^p n$", "numero de parte"]);
  const cSn = buscarColumna(encabezados, ["^serial", "^sn$", "^s n$", "serie"]);
  const cTsn = buscarColumna(encabezados, ["^tsn", "total time", "horas totales"]);
  const cTso = buscarColumna(encabezados, ["^tso", "since overhaul", "desde overhaul"]);
  const cCsn = buscarColumna(encabezados, ["^csn", "ciclos totales"]);
  const cLimH = buscarColumna(encabezados, ["limite hora", "^tbo", "vida", "limit hours", "limite$", "^limite"]);
  const cLimC = buscarColumna(encabezados, ["limite ciclo", "cycle limit", "ciclos limite"]);
  const cVenc = buscarColumna(encabezados, ["vencimiento", "^due", "next due", "expira", "fecha limite", "proximo"]);
  const cRest = buscarColumna(encabezados, ["restante", "remaining", "remanente"]);

  const items = [];
  filas.forEach((fila, i) => {
    try {
      if (vacia(fila)) return;
      const componente = aTexto(fila[cComp]);
      if (!componente) {
        cuarentena.push({ origen, fila: i + 2, motivo: "Fila sin nombre de componente", datos: (fila || []).slice(0, 5).map(aTexto).join(" | ") });
        return;
      }
      const tsn = cTsn >= 0 ? aNumero(fila[cTsn]) : null;
      const tso = cTso >= 0 ? aNumero(fila[cTso]) : null;
      const limiteHoras = cLimH >= 0 ? aNumero(fila[cLimH]) : null;
      let restanteHoras = cRest >= 0 ? aNumero(fila[cRest], { permitirNegativo: true }) : null;
      if (restanteHoras === null && limiteHoras !== null) {
        const usado = tso !== null ? tso : tsn;
        if (usado !== null) restanteHoras = +(limiteHoras - usado).toFixed(2);
      }
      items.push({
        componente,
        pn: cPn >= 0 ? aTexto(fila[cPn]) : "",
        sn: cSn >= 0 ? aTexto(fila[cSn]) : "",
        tsn, tso,
        csn: cCsn >= 0 ? aNumero(fila[cCsn]) : null,
        limiteHoras,
        limiteCiclos: cLimC >= 0 ? aNumero(fila[cLimC]) : null,
        vencimiento: cVenc >= 0 ? aFecha(fila[cVenc]) : null,
        restanteHoras,
        verificado: false,
      });
    } catch (e) {
      cuarentena.push({ origen, fila: i + 2, motivo: "Error al leer el componente: " + (e && e.message), datos: "" });
    }
  });
  if (!items.length) return null;

  // La célula manda el TSN/CSN de la aeronave; si no está, el mayor TSN leído.
  const celula = items.find((x) => /celula|airframe|fuselaje|helicoptero|aircraft/.test(normalizar(x.componente)));
  const totales = {
    tsn: celula?.tsn ?? (items.reduce((m, x) => (x.tsn !== null && x.tsn > m ? x.tsn : m), 0) || null),
    csn: celula?.csn ?? (items.reduce((m, x) => (x.csn !== null && x.csn > m ? x.csn : m), 0) || null),
  };
  return { items, totales };
}

/* -------------------------------------------------------------- construcción */

const ID_LIBRE = (s) => normalizar(s).replace(/[^a-z0-9]/g, "").slice(0, 24) || `tipo${Date.now()}`;

/**
 * Arma el perfil de tipo y la entrada de flota a partir de las hojas
 * clasificadas. `hojas` = [{ tipo, encabezados, filas, matriz, origen }].
 */
export function construirImportacion(hojas = []) {
  const cuarentena = [];
  const anuladas = [];
  let identificacion = {};
  let estaciones = null;
  let envelope = null;
  let ogeTable = null, igeTable = null, fuelTable = null;
  let status = null;

  for (const h of hojas) {
    if (!h || !h.tipo) continue;
    const enc = h.encabezados || [];
    const filas = h.filas || [];
    const matriz = h.matriz || [];
    const origen = h.origen || "";
    try {
      switch (h.tipo) {
        case "identificacion": {
          const r = leerIdentificacion(matriz);
          if (r) identificacion = { ...identificacion, ...r };
          break;
        }
        case "estaciones": {
          const r = leerEstaciones(enc, filas, cuarentena, origen);
          if (r) estaciones = estaciones
            ? { arms: { ...r.arms, ...estaciones.arms },
                cargoCompartments: [...estaciones.cargoCompartments, ...r.cargoCompartments],
                sinMapear: [...estaciones.sinMapear, ...r.sinMapear] }
            : r;
          break;
        }
        case "envolvente": {
          const r = leerEnvolvente(enc, filas, cuarentena, origen);
          if (r) envelope = r;
          break;
        }
        case "oge": case "ige": {
          const cruda = leerRejilla(matriz, cuarentena, origen);
          if (cruda) {
            const { rejilla, anuladas: an } = anularCeldasImposibles(cruda, h.tipo.toUpperCase());
            anuladas.push(...an);
            if (h.tipo === "oge") ogeTable = rejilla; else igeTable = rejilla;
          }
          break;
        }
        case "combustible": {
          const r = leerRejilla(matriz, cuarentena, origen);
          if (r) fuelTable = r; // el consumo no sigue la regla de monotonía de las cartas de peso
          break;
        }
        case "status": {
          const r = leerStatus(enc, filas, cuarentena, origen);
          if (r) status = status ? { items: [...status.items, ...r.items], totales: r.totales || status.totales } : r;
          break;
        }
        default: break;
      }
    } catch (e) {
      cuarentena.push({ origen, fila: "-", motivo: `La hoja completa falló (${h.tipo}): ${e && e.message}`, datos: "" });
    }
  }

  // Si la identificación no apareció en su hoja, se busca en todas las demás.
  if (!identificacion.matricula) {
    for (const h of hojas) {
      const r = leerIdentificacion(h?.matriz || []);
      if (r?.matricula) { identificacion = { ...r, ...identificacion }; break; }
    }
  }

  const tipoNombre = identificacion.tipo || "Tipo importado";
  const perfilId = `imp_${ID_LIBRE(tipoNombre)}`;

  const perfil = {
    id: perfilId,
    tipo: tipoNombre,
    // El peso vacío del TIPO no se toma del pesaje de una matrícula: ese valor
    // pertenece a esa aeronave y viaja en la flota.
    emptyKg: null,
    emptyArm: null,
    arms: estaciones?.arms || {},
    fuelDensity: 0.8,
    fuelCapacityL: identificacion.fuelCapacityL ?? null,
    ogeTable, igeTable, fuelTable,
    maxInternalKg: identificacion.pesoMaxDespegueKg ?? null,
    maxExternalKg: null,
    envelope,
    cargoCompartments: estaciones?.cargoCompartments || [],
    datumNote: "",
    procedencia: {
      origen: "importado de planilla del operador",
      fecha: new Date().toISOString().slice(0, 10),
      verificado: false,
      nota: "Ningún valor de este perfil fue cotejado contra el RFM. Verificar celda por celda antes de usarlo para despachar.",
    },
  };

  const aeronave = identificacion.matricula ? {
    id: `imp_${ID_LIBRE(identificacion.matricula)}`,
    matricula: identificacion.matricula,
    tipo: tipoNombre,
    serie: identificacion.serie || "",
    profileId: perfilId,
    tsn: status?.totales?.tsn ?? identificacion.tsn ?? null,
    csn: status?.totales?.csn ?? identificacion.csn ?? null,
    ldg: 0, cargas: 0,
    motor: (identificacion.motorTipo || identificacion.motorSerie) ? {
      tipo: identificacion.motorTipo || "",
      serie: identificacion.motorSerie || "",
      tsn: status?.totales?.tsn ?? null,
      cyNG: null, cyNF: null,
    } : null,
    pesoBasicoVacioKg: identificacion.pesoBasicoVacioKg ?? null,
    fechaPesaje: identificacion.fechaPesaje ?? null,
    // null a propósito cuando la planilla no lo trae: sin brazo no hay CG, y la
    // app tiene que decirlo en vez de heredar el del tipo.
    brazoVacioM: identificacion.brazoVacioM ?? null,
    pesoMaxDespegueKg: identificacion.pesoMaxDespegueKg ?? null,
    pesoCeroCombustibleKg: identificacion.pesoCeroCombustibleKg ?? null,
    estadoInicial: {
      referencia: `Importado de planilla el ${new Date().toISOString().slice(0, 10)}`,
      tsn: status?.totales?.tsn ?? null,
      csn: status?.totales?.csn ?? null,
      ldg: 0, cargas: 0,
    },
    verificado: false,
  } : null;

  return { perfil, aeronave, mantenimiento: status?.items || [], cuarentena, anuladas };
}

/* --------------------------------------------------------------- validación */

/**
 * Separa lo que impide usar la importación (errores) de lo que exige atención
 * pero no bloquea (avisos).
 */
export function validarImportacion({ perfil, aeronave, mantenimiento = [], anuladas = [] } = {}, hoy = new Date().toISOString().slice(0, 10)) {
  const errores = [];
  const avisos = [];

  if (!aeronave) errores.push("No se encontró la matrícula en ninguna hoja. Sin matrícula no se puede crear la aeronave.");
  if (!perfil) { errores.push("No se pudo armar el perfil de tipo."); return { errores, avisos }; }

  const arms = perfil.arms || {};
  const faltantes = ["pilot", "paxFront", "paxRear", "fuel"].filter((k) => arms[k] === undefined);
  if (faltantes.length) errores.push(`Faltan brazos obligatorios para el peso y balance: ${faltantes.join(", ")}.`);
  for (const [k, v] of Object.entries(arms)) {
    if (Math.abs(v) > 20) avisos.push(`Brazo poco creíble en "${k}": ${v} m. ¿La planilla estaba en pulgadas sin decirlo?`);
  }

  const env = perfil.envelope;
  if (!env) errores.push("No se cargó la envolvente de CG. Sin ella no hay verificación de centro de gravedad.");
  else {
    if (env.forward.length < 2) errores.push("La envolvente tiene menos de 2 puntos en el límite delantero.");
    if (!env.rearwardInternal.length) errores.push("La envolvente no tiene límite trasero.");
    for (const [p, cg] of env.forward) {
      const par = env.rearwardInternal.find((x) => x[0] === p);
      if (par && cg >= par[1]) errores.push(`Envolvente a ${p} kg: el límite delantero (${cg}) no es menor que el trasero (${par[1]}).`);
    }
  }

  if (!perfil.ogeTable && !perfil.igeTable) avisos.push("No se cargó ninguna carta de peso máximo (OGE/IGE): la pestaña Performance dirá \"sin dato\".");
  if (!perfil.fuelTable) avisos.push("No se cargó la carta de consumo: los tramos dirán \"fuera de carta\".");
  if (anuladas.length) {
    avisos.push(`${anuladas.length} celda(s) de performance se anularon por violar la física de la carta (el peso máximo no puede subir con la temperatura ni con la altitud). Releer esas celdas del RFM.`);
  }

  if (aeronave) {
    if (aeronave.pesoBasicoVacioKg == null) errores.push("Falta el peso básico vacío de la matrícula (sale del pesaje, no del tipo).");
    if (aeronave.pesoMaxDespegueKg == null) errores.push("Falta el peso máximo de despegue de la matrícula.");
    if (aeronave.brazoVacioM == null) avisos.push("La planilla no trae el brazo del peso vacío. El CG quedará sin calcular hasta cargarlo del acta de pesaje — no se hereda el del tipo.");
    if (aeronave.pesoBasicoVacioKg != null && aeronave.pesoMaxDespegueKg != null && aeronave.pesoBasicoVacioKg >= aeronave.pesoMaxDespegueKg) {
      errores.push(`El peso vacío (${aeronave.pesoBasicoVacioKg} kg) es mayor o igual al peso máximo de despegue (${aeronave.pesoMaxDespegueKg} kg). Revisa si uno de los dos está en libras.`);
    }
    // Red de seguridad de la regla del punto decimal (ver aNumero): si una
    // planilla escribía los miles con punto, el peso sale mil veces menor.
    if (aeronave.pesoBasicoVacioKg != null && aeronave.pesoBasicoVacioKg < 200) {
      avisos.push(`Peso básico vacío increíblemente bajo (${aeronave.pesoBasicoVacioKg} kg). Si la planilla escribe los miles con punto, el valor se leyó mil veces menor: verificarlo.`);
    }
    if (aeronave.tsn == null) avisos.push("No se leyó el TSN de la aeronave: el arrastre de la bitácora arrancará sin línea base.");
    if (aeronave.fechaPesaje && aeronave.fechaPesaje > hoy) avisos.push(`La fecha de pesaje (${aeronave.fechaPesaje}) es futura.`);
  }

  const vencidos = mantenimiento.filter((m) => m.restanteHoras !== null && m.restanteHoras < 0);
  if (vencidos.length) errores.push(`${vencidos.length} componente(s) con horas vencidas: ${vencidos.slice(0, 3).map((m) => m.componente).join(", ")}${vencidos.length > 3 ? "…" : ""}`);
  const porVencer = mantenimiento.filter((m) => m.restanteHoras !== null && m.restanteHoras >= 0 && m.restanteHoras <= 25);
  if (porVencer.length) avisos.push(`${porVencer.length} componente(s) con 25 h o menos de remanente.`);
  const calVencidos = mantenimiento.filter((m) => m.vencimiento && m.vencimiento < hoy);
  if (calVencidos.length) errores.push(`${calVencidos.length} componente(s) con vencimiento calendario pasado.`);

  avisos.push("Importación NO verificada: ningún valor fue cotejado contra el RFM ni contra el acta de pesaje.");
  return { errores, avisos };
}
